import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useDeleteQuoteTemplate, useQuoteTemplates, type QuoteTemplateRow } from "@/hooks/useQuoteTemplates";
import { InlineConfirm } from "./InlineConfirm";

interface TemplatePopoverProps {
  /** The quote's current line count: with items, loading asks Replace or Add. */
  itemCount: number;
  onLoad: (template: QuoteTemplateRow, mode: "replace" | "add") => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Load template in the add bar: pick one, then Replace them / Add to them / Cancel (#1559). */
export function TemplatePopover({ itemCount, onLoad }: TemplatePopoverProps) {
  const { data: templates, isError } = useQuoteTemplates();
  const deleteTemplate = useDeleteQuoteTemplate();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<QuoteTemplateRow | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const reset = (o: boolean) => {
    setOpen(o);
    setAsking(null);
    setDeleting(null);
  };
  const load = (t: QuoteTemplateRow, mode: "replace" | "add") => {
    onLoad(t, mode);
    if (itemCount === 0) toast.success(`Loaded "${t.name}". Change anything you like.`);
    else toast.success(mode === "replace" ? `Replaced with "${t.name}".` : `Added "${t.name}".`);
    reset(false);
  };
  const pick = (t: QuoteTemplateRow) => (itemCount > 0 ? setAsking(t) : load(t, "replace"));

  return (
    <Popover open={open} onOpenChange={reset}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8">Load template</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="grid w-[min(330px,calc(100vw-24px))] gap-0.5 p-1.5">
        {asking ? (
          <div className="grid gap-2 rounded-[7px] bg-accent-soft px-2.5 py-2 text-[13px]">
            Load "{asking.name}". This quote already has {plural(itemCount, "item")}.
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={() => setAsking(null)}>Cancel</Button>
              <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={() => load(asking, "add")}>Add to them</Button>
              <Button type="button" size="sm" className="h-8" onClick={() => load(asking, "replace")}>Replace them</Button>
            </div>
          </div>
        ) : isError ? (
          <p className="px-2 py-1.5 text-[12.5px] text-muted-foreground">Couldn't load templates.</p>
        ) : !templates?.length ? (
          <p className="px-2 py-1.5 text-[12.5px] text-muted-foreground">No templates yet.</p>
        ) : (
          templates.map((t) =>
            deleting === t.id ? (
              <InlineConfirm
                key={t.id}
                tone="accent"
                text={`Delete the "${t.name}" template? Quotes made from it keep their items.`}
                cancelLabel="Keep it"
                confirmLabel="Delete template"
                busy={deleteTemplate.isPending}
                onCancel={() => setDeleting(null)}
                onConfirm={() => deleteTemplate.mutate(t.id, { onSettled: () => setDeleting(null) })}
              />
            ) : (
              <div key={t.id} className="flex items-center gap-1.5 rounded-md hover:bg-muted">
                <button
                  type="button"
                  disabled={!t.items?.length}
                  onClick={() => pick(t)}
                  className="grid flex-1 px-2 py-1.5 text-left text-[13.5px] disabled:opacity-50"
                >
                  {t.name}
                  <small className="text-[12px] text-muted-foreground">{plural(t.items?.length ?? 0, "item")}</small>
                </button>
                <button
                  type="button"
                  aria-label={`Delete template ${t.name}`}
                  onClick={() => setDeleting(t.id)}
                  className="px-1.5 text-muted-foreground hover:text-status-hot"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ),
          )
        )}
      </PopoverContent>
    </Popover>
  );
}
